'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import {
  Image,
  Cpu,
  Search,
  Radar,
  ArrowDownRight,
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  Play,
  X,
} from 'lucide-react';

/* ─── Hero event data (matches real /pulse category colours) ─── */

const HERO_EVENTS = [
  {
    id: 'h1',
    company: 'NexusPay',
    domain: 'nexuspay.io',
    signal: 'Pricing Pivot',
    detail: 'Annual plan dropped by 15%',
    status: 'High Impact',
    category: 'visual',
    severity: 'high',
    statusColor: 'text-amber-500',
    taskLabel: 'Sent to Asana — High Priority',
    serp: { keyword: 'fintech pricing', from: 3, to: 9 },
  },
  {
    id: 'h2',
    company: 'Vertex Bank',
    domain: 'vertexbank.com',
    signal: 'Tech Stack Shift',
    detail: 'Migrated to Next.js; Speed +20%',
    status: 'Strategic',
    category: 'tech',
    severity: 'medium',
    statusColor: 'text-shield-cyan',
    taskLabel: 'Sent to Slack — #eng-intel',
    serp: { keyword: 'digital banking', from: 5, to: 11 },
  },
  {
    id: 'h3',
    company: 'CloudFlow',
    domain: 'cloudflow.dev',
    signal: 'Position Shift',
    detail: "Now #1 for 'AI Workflow'",
    status: 'Critical',
    category: 'search',
    severity: 'high',
    statusColor: 'text-shield-cyan',
    taskLabel: 'Sent to Asana — SEO Review',
    serp: { keyword: 'ai workflow', from: 8, to: 1 },
  },
  {
    id: 'h4',
    company: 'StreamlineAI',
    domain: 'streamline.ai',
    signal: 'Content Strategy',
    detail: 'New /vs/ comparison pages detected for 4 competitors',
    status: 'Watch',
    category: 'strategy',
    severity: 'medium',
    statusColor: 'text-amber-500',
    taskLabel: 'Sent to Slack — #content',
    serp: { keyword: 'ai automation', from: 12, to: 6 },
  },
  {
    id: 'h5',
    company: 'SwiftCart',
    domain: 'swiftcart.com',
    signal: 'Visual Change',
    detail: 'CTA changed from "Start Free" to "Book a Demo"',
    status: 'Detected',
    category: 'visual',
    severity: 'low',
    statusColor: 'text-violet-400',
    taskLabel: null,
    serp: null,
  },
];

/* ─── Pulse category config (mirrors real /pulse PulseCard.jsx) ─── */

const CATEGORY_CONFIG = {
  visual: { label: 'Visual', icon: Image, color: 'text-violet-500', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  tech: { label: 'Tech', icon: Cpu, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  search: { label: 'Search', icon: Search, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  strategy: { label: 'Strategy', icon: Radar, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
};

const ADVANCE_INTERVAL = 4000; // faster than lower section (cinematic pace)
const MAX_VISIBLE = 3;

const severityGlow = {
  high: '0 0 12px rgba(79,209,229,0.4)',
  medium: 'none',
  low: 'none',
};

/* ─── Heartbeat dot (mirrors HeartbeatIndicator.jsx) ─── */

function LiveBadge() {
  const [beat, setBeat] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setBeat(true);
      setTimeout(() => setBeat(false), 600);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex h-2.5 w-2.5 items-center justify-center">
        <motion.span
          className="absolute inline-flex h-full w-full rounded-full bg-shield-cyan"
          animate={beat ? { scale: [1, 2.2], opacity: [0.7, 0] } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-shield-cyan" />
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-shield-cyan">
        Live
      </span>
    </div>
  );
}

/* ─── Status Dot — scanning → detecting → verified ─── */

function StatusDot({ isNew }) {
  const [phase, setPhase] = useState(isNew ? 'scanning' : 'verified');

  useEffect(() => {
    if (!isNew) return;
    const t1 = setTimeout(() => setPhase('detecting'), 500);
    const t2 = setTimeout(() => setPhase('verified'), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isNew]);

  return (
    <motion.div
      className="w-2.5 h-2.5 rounded-full shrink-0 mt-1"
      animate={
        phase === 'scanning'
          ? { backgroundColor: '#9ca3af', opacity: [0.4, 0.8, 0.4] }
          : phase === 'detecting'
            ? { backgroundColor: '#F5B942', opacity: [1, 0.3, 1] }
            : { backgroundColor: '#4FD1E5', opacity: 1 }
      }
      transition={
        phase === 'scanning'
          ? { repeat: 2, duration: 0.3 }
          : phase === 'detecting'
            ? { repeat: 1, duration: 0.25 }
            : { duration: 0.3 }
      }
    />
  );
}

/* ─── Category tag badge (mirrors PulseCard CategoryTag) ─── */

function CategoryTag({ category }) {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.visual;
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${config.bg} ${config.border} ${config.color}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {config.label}
    </span>
  );
}

/* ─── SERP Movement (mirrors PulseCard SerpMovement) ─── */

function SerpMovement({ serp }) {
  if (!serp) return null;
  const { from, to } = serp;
  const diff = from - to; // positive = improved (lower number = better)
  const improved = diff > 0;
  const Icon = improved ? ArrowUpRight : ArrowDownRight;
  const color = improved ? 'text-emerald-500' : 'text-red-500';

  return (
    <div className="flex items-center gap-1 mt-1.5">
      <span className="text-[10px] text-muted-foreground">#{from}</span>
      <Icon className={`h-3 w-3 ${color}`} />
      <span className={`text-[10px] font-semibold ${color}`}>#{to}</span>
      <span className={`text-[9px] ${color}`}>
        ({diff > 0 ? '+' : ''}{diff})
      </span>
      <span className="text-[9px] text-muted-foreground ml-1">&quot;{serp.keyword}&quot;</span>
    </div>
  );
}

/* ─── Single Event Card ─── */

function HeroEventCard({ event, isNew, isLast, totalVisible }) {
  // Fade cards progressively: newest = full opacity, oldest = dimmed
  const restOpacity = isLast && totalVisible >= MAX_VISIBLE ? 0.4 : 1;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
      animate={{
        opacity: restOpacity,
        height: 'auto',
        marginBottom: 8,
        boxShadow: isNew
          ? [
              '0 0 0px rgba(79,209,229,0)',
              '0 0 20px rgba(79,209,229,0.35)',
              '0 0 0px rgba(79,209,229,0)',
            ]
          : 'none',
      }}
      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
      transition={{
        opacity: { duration: 0.5 },
        height: { duration: 0.3 },
        marginBottom: { duration: 0.3 },
        boxShadow: { duration: 2, ease: 'easeOut' },
      }}
      className={`rounded-xl border bg-card p-3 overflow-hidden transition-colors ${
        isNew
          ? 'border-shield-cyan/40 dark:shadow-[0_0_12px_rgba(79,209,229,0.06)]'
          : 'border-border/40 hover:border-border/60'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <StatusDot isNew={isNew} />
        <div className="min-w-0 flex-1">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <img
                src={`https://www.google.com/s2/favicons?domain=${event.domain}&sz=32`}
                alt=""
                width={14}
                height={14}
                className="rounded-sm shrink-0"
              />
              <span className="text-[11px] font-semibold text-foreground truncate">
                {event.company}
              </span>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                {event.domain}
              </span>
            </div>
            <CategoryTag category={event.category} />
          </div>

          {/* Detail text */}
          <p className="text-[11px] text-muted-foreground leading-snug mt-1.5">
            {event.detail}
          </p>

          {/* SERP movement if present */}
          <SerpMovement serp={event.serp} />

          {/* Status + task row */}
          <div className="flex items-center justify-between mt-2">
            <span className={`text-[9px] font-semibold uppercase ${event.statusColor}`}>
              {event.status}
            </span>
            {event.taskLabel && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="flex items-center gap-1 text-[9px] text-shield-cyan"
              >
                <CheckCircle2 className="h-2.5 w-2.5" />
                <span className="hidden sm:inline">{event.taskLabel}</span>
                <span className="sm:hidden">Task created</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Hero Feed — stacking cards like live-pulse-feed ─── */

function HeroFeed() {
  const [visibleEvents, setVisibleEvents] = useState([]);
  const eventIndexRef = useRef(0);
  const timerRef = useRef(null);

  const advanceEvent = useCallback(() => {
    setVisibleEvents((prev) => {
      const nextIdx = eventIndexRef.current % HERO_EVENTS.length;
      const nextEvent = { ...HERO_EVENTS[nextIdx], _key: `hero-${eventIndexRef.current}` };
      eventIndexRef.current += 1;

      const updated = [nextEvent, ...prev];
      if (updated.length > MAX_VISIBLE) {
        return updated.slice(0, MAX_VISIBLE);
      }
      return updated;
    });
  }, []);

  useEffect(() => {
    // First card appears quickly
    const t0 = setTimeout(() => advanceEvent(), 800);
    timerRef.current = setInterval(advanceEvent, ADVANCE_INTERVAL);

    return () => {
      clearTimeout(t0);
      clearInterval(timerRef.current);
    };
  }, [advanceEvent]);

  return (
    <div className="w-full max-w-md">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <LiveBadge />
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Sparkles className="h-3 w-3 text-shield-cyan" />
          <span>Continuous Scanning Active</span>
        </div>
      </div>

      {/* Feed container — fixed height to prevent layout shift */}
      <div className="h-[420px] overflow-hidden relative">
        <div>
          <AnimatePresence initial={false}>
            {visibleEvents.map((event, i) => (
              <HeroEventCard
                key={event._key}
                event={event}
                isNew={i === 0}
                isLast={i === visibleEvents.length - 1}
                totalVisible={visibleEvents.length}
              />
            ))}
          </AnimatePresence>
        </div>

        {visibleEvents.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-shield-cyan/30 animate-pulse" />
              Scanning competitive landscape...
            </div>
          </div>
        )}

        {/* Bottom fade mask */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </div>

      {/* AI correlation footer — always present, fades in */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: visibleEvents.length >= 2 ? 1 : 0 }}
        transition={{ duration: 0.5 }}
        className="mt-3 rounded-lg bg-shield-cyan/5 border border-shield-cyan/10 p-2.5 flex items-start gap-2"
      >
        <Sparkles className="h-3.5 w-3.5 text-shield-cyan shrink-0 mt-0.5" />
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          <span className="text-shield-cyan font-medium">AI Insight:</span>{' '}
          Pricing changes correlated with SERP movement across 2 competitors.
          Pattern suggests coordinated market repositioning.
        </p>
      </motion.div>
    </div>
  );
}

/* ─── Demo Video Dialog ─── */

function DemoVideoDialog() {
  const [open, setOpen] = useState(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button
          variant="outline"
          size="lg"
          className="border-border text-foreground hover:bg-secondary font-medium px-8 bg-transparent gap-2"
        >
          <Play className="h-4 w-4" />
          See how it works
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-4 sm:inset-8 z-50 flex items-center justify-center rounded-xl overflow-hidden bg-black shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <DialogPrimitive.Title className="sr-only">
            Tivion Demo
          </DialogPrimitive.Title>
          {open && (
            <iframe
              src="https://www.youtube-nocookie.com/embed/RDK8IANLVgE?autoplay=1&rel=0&modestbranding=1"
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="w-full h-full"
              title="Tivion Demo"
            />
          )}
          <DialogPrimitive.Close className="absolute right-3 top-3 rounded-full bg-black/60 p-1.5 text-white/80 hover:text-white hover:bg-black/80 transition-colors focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ─── Hero Section ─── */

export function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background noise texture */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-50" />

      {/* Subtle starfield (deterministic to avoid hydration mismatch) */}
      <div className="absolute inset-0">
        {Array.from({ length: 50 }).map((_, i) => {
          const seed = i * 7 + 3;
          return (
            <div
              key={i}
              className="absolute rounded-full bg-foreground/20"
              style={{
                width: ((seed * 13) % 20) / 10 + 1 + 'px',
                height: ((seed * 17) % 20) / 10 + 1 + 'px',
                top: ((seed * 31) % 100) + '%',
                left: ((seed * 37) % 100) + '%',
              }}
            />
          );
        })}
      </div>

      {/* Masked gradient glow behind animation */}
      <div className="absolute right-0 top-1/4 w-1/2 h-1/2 bg-gradient-radial from-shield-cyan/5 via-transparent to-transparent blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight text-balance">
              Architect Your Market&apos;s Digital Landscape.
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-xl">
              Tivion correlates SERP shifts, visual changes, and tech-stack pivots into a single
              stream of intelligence. Know exactly when a rival moves—and why.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-shield-cyan text-primary-foreground hover:bg-shield-cyan/90 font-medium px-8"
                asChild
              >
                <Link href="/onboarding/identity">Sign Up</Link>
              </Button>
              <DemoVideoDialog />
            </div>
          </div>

          {/* Animated Feed */}
          <div className="relative flex justify-center lg:justify-end">
            <HeroFeed />
          </div>
        </div>
      </div>
    </section>
  );
}
